"use client";

import * as React from "react";
import {
  Text,
  SectionCard,
  CardTitle,
  CardDescription,
  Section,
  DropdownMenu,
} from "@/src/components";
import { Edit, Trash, Copy, Settings, User, LogOut, ChevronDown, MoreHorizontal, Sun, Moon, Monitor } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsDropdownMenu() {
  const [showHidden, setShowHidden] = React.useState(false);
  const [notifications, setNotifications] = React.useState(true);
  const [theme, setTheme] = React.useState("system");
  const [sortBy, setSortBy] = React.useState("date");

  return (
    <>
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Dropdown Menu
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Displays a menu with actions when triggered. Supports items with icons,
            checkboxes, radio groups, submenus, separators, and labels.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/dropdown-menu" />
        </SectionCard>
      </Section>

      {/* Basic Example */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Menu</CardTitle>
              <CardDescription>Simple dropdown with action items and icons</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex gap-4">
            <DropdownMenu
              trigger={{ label: "Actions", icon: <ChevronDown className="h-4 w-4" />, variant: "outline" }}
              items={[
                { type: "item", label: "Edit", icon: <Edit className="h-4 w-4" />, onClick: () => console.log("Edit") },
                { type: "item", label: "Duplicate", icon: <Copy className="h-4 w-4" />, onClick: () => console.log("Duplicate") },
                { type: "separator" },
                { type: "item", label: "Delete", icon: <Trash className="h-4 w-4" />, disabled: true },
              ]}
            />
            <DropdownMenu
              trigger={{ icon: <MoreHorizontal className="h-4 w-4" />, variant: "ghost", iconOnly: true }}
              items={[
                { type: "item", label: "View Profile", onClick: () => {} },
                { type: "item", label: "Settings", onClick: () => {} },
                { type: "separator" },
                { type: "item", label: "Logout", onClick: () => {} },
              ]}
              align="end"
            />
          </div>
        </SectionCard>
      </Section>

      {/* Checkbox Items */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Checkbox Items</CardTitle>
              <CardDescription>Toggle options on/off with checkboxes</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <DropdownMenu
            trigger={{ label: "View Options", variant: "outline" }}
            items={[
              { type: "label", label: "Display" },
              { type: "checkbox", label: "Show Hidden Files", checked: showHidden, onClick: () => setShowHidden(!showHidden) },
              { type: "checkbox", label: "Enable Notifications", checked: notifications, onClick: () => setNotifications(!notifications) },
              { type: "separator" },
              { type: "item", label: "Reset to Defaults", onClick: () => { setShowHidden(false); setNotifications(true); } },
            ]}
          />
          <Text className="mt-4 text-sm text-muted-foreground">
            Hidden: {showHidden ? "Yes" : "No"} | Notifications: {notifications ? "On" : "Off"}
          </Text>
        </SectionCard>
      </Section>

      {/* Radio Group */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Radio Group</CardTitle>
              <CardDescription>Select one option from a group</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex gap-4">
            <DropdownMenu
              trigger={{ label: `Theme: ${theme}`, variant: "outline" }}
              items={[
                {
                  type: "radioGroup",
                  label: "Theme",
                  value: theme,
                  onValueChange: setTheme,
                  options: [
                    { label: "Light", value: "light" },
                    { label: "Dark", value: "dark" },
                    { label: "System", value: "system" },
                  ],
                },
              ]}
            />
            <DropdownMenu
              trigger={{ label: `Sort: ${sortBy}`, variant: "outline" }}
              items={[
                {
                  type: "radioGroup",
                  label: "Sort By",
                  value: sortBy,
                  onValueChange: setSortBy,
                  options: [
                    { label: "Name", value: "name" },
                    { label: "Date", value: "date" },
                    { label: "Size", value: "size" },
                  ],
                },
              ]}
            />
          </div>
        </SectionCard>
      </Section>

      {/* Submenu */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Submenu</CardTitle>
              <CardDescription>Nested menus for complex navigation</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <DropdownMenu
            trigger={{ label: "More Options", variant: "outline" }}
            items={[
              { type: "item", label: "New File", onClick: () => {} },
              { type: "item", label: "Open", onClick: () => {} },
              { type: "separator" },
              {
                type: "submenu",
                label: "Share",
                children: [
                  { type: "item", label: "Email", onClick: () => {} },
                  { type: "item", label: "Copy Link", onClick: () => {} },
                  { type: "separator" },
                  { type: "item", label: "Slack", onClick: () => {} },
                  { type: "item", label: "Teams", onClick: () => {} },
                ],
              },
              {
                type: "submenu",
                label: "Export As",
                children: [
                  { type: "item", label: "PDF", onClick: () => {} },
                  { type: "item", label: "CSV", onClick: () => {} },
                  { type: "item", label: "JSON", onClick: () => {} },
                ],
              },
            ]}
          />
        </SectionCard>
      </Section>

      {/* Trigger Variants */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Trigger Variants</CardTitle>
              <CardDescription>Different trigger button styles and sizes</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex flex-wrap gap-4">
            <DropdownMenu
              trigger={{ label: "Default", variant: "default" }}
              items={[{ type: "item", label: "Option 1" }, { type: "item", label: "Option 2" }]}
            />
            <DropdownMenu
              trigger={{ label: "Outline", variant: "outline" }}
              items={[{ type: "item", label: "Option 1" }, { type: "item", label: "Option 2" }]}
            />
            <DropdownMenu
              trigger={{ label: "Ghost", variant: "ghost" }}
              items={[{ type: "item", label: "Option 1" }, { type: "item", label: "Option 2" }]}
            />
            <DropdownMenu
              trigger={{ label: "Small", variant: "outline", size: "sm" }}
              items={[{ type: "item", label: "Option 1" }, { type: "item", label: "Option 2" }]}
            />
            <DropdownMenu
              trigger={{ label: "Large", variant: "outline", size: "lg" }}
              items={[{ type: "item", label: "Option 1" }, { type: "item", label: "Option 2" }]}
            />
          </div>
        </SectionCard>
      </Section>

      {/* Props Reference */}
      <Section>
        <SectionCard
          header={<CardTitle>Props Reference</CardTitle>}
          contentClassName="p-6"
        >
          <div className="space-y-6">
            <div>
              <Text className="font-semibold mb-2">DropdownMenuConfig</Text>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-semibold">Prop</th>
                    <th className="text-left py-2 font-semibold">Type</th>
                    <th className="text-left py-2 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">trigger</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">TriggerConfig</td>
                    <td className="py-2 text-muted-foreground">Trigger button configuration (label, icon, variant, size, iconOnly)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">items</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">DropdownMenuItemConfig[]</td>
                    <td className="py-2 text-muted-foreground">Array of menu items</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">align</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">"start" | "center" | "end"</td>
                    <td className="py-2 text-muted-foreground">Horizontal alignment of menu</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">side</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">"top" | "right" | "bottom" | "left"</td>
                    <td className="py-2 text-muted-foreground">Side to open menu on</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <Text className="font-semibold mb-2">Item Types</Text>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-semibold">Type</th>
                    <th className="text-left py-2 font-semibold">Properties</th>
                    <th className="text-left py-2 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">item</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">label, icon, shortcut, disabled, onClick</td>
                    <td className="py-2 text-muted-foreground">Clickable action item</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">separator</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">-</td>
                    <td className="py-2 text-muted-foreground">Visual divider line</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">label</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">label</td>
                    <td className="py-2 text-muted-foreground">Section heading text</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">checkbox</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">label, checked, disabled, onClick</td>
                    <td className="py-2 text-muted-foreground">Toggle option</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">radioGroup</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">label, value, onValueChange, options[]</td>
                    <td className="py-2 text-muted-foreground">Single selection from options</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">submenu</td>
                    <td className="py-2 font-mono text-xs text-muted-foreground">label, children[]</td>
                    <td className="py-2 text-muted-foreground">Nested menu items</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </SectionCard>
      </Section>
    </>
  );
}
